import { useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Einheit } from "@kundeninfo/api-client";
import { Arbeitsdialog, darfAbbrechen, Objektliste, Objektzeile, useBeforeUnload, useBestaetigung, useFormularZustand }
  from "@kundeninfo/ui";
import { Eingabe, Formular, Schalter } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { Meldung, meldungAus } from "./gemeinsam.js";
import { Loaded, useMasterData } from "./accounts/masterData.js";
import { UNIT_TEXT as T } from "./unitTexts.js";

/**
 * Verwaltung › EINHEITEN (Spec Artikelmodul 0.4, „Verwaltung › Einheiten (779)“): der konzernweite Einheitenkatalog
 * samt stillgelegten, als Liste aus `Objektzeile` (Name, UN/ECE-Code, „n Artikel“, Status „stillgelegt“). Bearbeitet
 * wird in derselben Hülle wie der Artikel-Dialog (`Arbeitsdialog panel="einheit"`, keine dritte Bauform) mit einem
 * einspaltigen Formular aus `<label for>`-Paaren (121–133). Jede Änderung fragt vorher gestapelt (Hausregel
 * „Bestätigung vor Datenänderung“); Löschen gibt es nur für ungenutzte und nicht gesäte Einheiten, alle anderen werden
 * stillgelegt. Liste, Entwurf und „Neu laden“ nach 409 kommen aus dem gemeinsamen Bau der Stammdaten (masterData.tsx).
 */
export type EinheitenClient = Pick<ApiClient, "listUnits" | "createUnit" | "updateUnit" | "deleteUnit">;

type Entwurf = { id: string | null; version: number; name: string; uneceCode: string; sortIndex: string;
  active: boolean; usage: number; seeded: boolean };

const FELDER = { name: T.name, uneceCode: T.code, sortIndex: T.sortIndex };

const entwurfAus = (e: Einheit | null): Entwurf => (e === null
  ? { id: null, version: 0, name: "", uneceCode: "", sortIndex: "", active: true, usage: 0, seeded: false }
  : { id: e.id, version: e.version, name: e.name, uneceCode: e.uneceCode ?? "", sortIndex: String(e.sortIndex),
      active: e.active, usage: e.usage, seeded: e.seeded });

/** Die Reihenfolge aus dem Feld: eine ganze Zahl ab 0. Leer heißt bei einer NEUEN Einheit „hinten anhängen“
 *  (`undefined`, der Server vergibt MAX + 10, I-2) — nie 0, sonst stünde sie vor der Saat und würde die Vorgabe jedes
 *  neuen Artikels (P17). Eine bestehende Einheit hat immer eine Reihenfolge: leer ist dort ein Feldfehler. */
function reihenfolge(text: string, neu: boolean): number | undefined {
  const roh = text.trim();
  if (roh === "" && neu) return undefined;
  if (!/^\d+$/.test(roh)) throw new ApiError(422, "EINGABE_UNLESBAR", T.sortInvalid, [{ field: "sortIndex", message: T.sortInvalid }]);
  return Number(roh);
}

export function VerwaltungEinheiten(p: { client: EinheitenClient }): ReactElement {
  const { client } = p;
  const m = useMasterData(() => client.listUnits({ includeInactive: true }), "einheiten", entwurfAus);
  return (
    <div className="vwe">
      <div className="vw__leiste">
        <KiKnopf art="primaer" onClick={() => m.open(null)}>{T.newUnit}</KiKnopf>
      </div>
      <Loaded list={m}>{(zeilen) => (zeilen.length === 0 ? <p className="vw__hinweis">{T.empty}</p> : (
        <Objektliste label={T.listLabel}>
          {zeilen.map((e) => (
            <Objektzeile key={e.id} name={e.name} gewaehlt={m.draft?.id === e.id} meta={T.usage(e.usage)}
              {...(e.uneceCode === null ? {} : { kennung: e.uneceCode })}
              {...(e.active ? {} : { status: { text: T.inactive, art: "aus" as const } })}
              ariaLabel={T.row(e.name, T.usage(e.usage))} onOeffnen={() => m.open(e)} />
          ))}
        </Objektliste>
      ))}</Loaded>
      {m.draft !== null && (
        <EinheitPanel key={m.draft.id ?? "neu"} client={client} entwurf={m.draft}
          aktivGespeichert={m.rows?.find((e) => e.id === m.draft?.id)?.active ?? true}
          titel={m.draft.id === null ? T.newUnit : T.editTitle(m.rows?.find((e) => e.id === m.draft?.id)?.name ?? m.draft.name)} dirty={m.dirty} aendere={m.change} neuLaden={m.refresh}
          gespeichert={async () => { await m.reload(); m.close(); }} schliessen={m.close} />
      )}
    </div>
  );
}

function EinheitPanel(p: {
  client: EinheitenClient; entwurf: Entwurf; titel: string; dirty: boolean;
  /** Der gespeicherte Zustand — „stilllegen?“ fragt nur der Wechsel von aktiv auf stillgelegt. */
  aktivGespeichert: boolean; aendere: (patch: Partial<Entwurf>) => void;
  neuLaden: () => Promise<void>; gespeichert: () => Promise<void>; schliessen: () => void;
}): ReactElement {
  const d = p.entwurf;
  const neu = d.id === null;
  const { bestaetigen } = useBestaetigung();
  useBeforeUnload(p.dirty);
  const formular = useFormularZustand<boolean>({
    speichern: async () => {
      const sortIndex = reihenfolge(d.sortIndex, neu);
      const eingabe = { name: d.name.trim(), uneceCode: d.uneceCode.trim() === "" ? null : d.uneceCode.trim(),
        ...(sortIndex === undefined ? {} : { sortIndex }) };
      // Vor jeder Änderung gestapelt fragen; das Stilllegen sagt, was es bewirkt.
      const stilllegen = !neu && p.aktivGespeichert && !d.active;
      const frage = neu ? { titel: T.confirmCreate(eingabe.name), text: T.saveConsequence }
        : stilllegen ? { titel: T.confirmDeactivate(eingabe.name), text: T.deactivateConsequence }
          : { titel: T.confirmSave(eingabe.name), text: T.saveConsequence };
      if (!(await bestaetigen({ ...frage, bestaetigenText: T.yes, abbrechenText: T.no }))) return false;
      if (d.id === null) await p.client.createUnit(eingabe);
      else await p.client.updateUnit(d.id, { ...eingabe, sortIndex: sortIndex!, version: d.version, active: d.active });
      return true;
    },
    neuLaden: p.neuLaden, bekannteFelder: Object.keys(FELDER), beschriftungen: FELDER,
  });
  const [loeschFehler, setLoeschFehler] = useState<string | null>(null);
  const laeuft = formular.laeuft;
  const meldung = loeschFehler ?? formular.meldung;
  const fehler = (f: keyof typeof FELDER) => (formular.feldFehler[f] === undefined ? {} : { fehler: formular.feldFehler[f] });

  async function speichern(): Promise<void> {
    setLoeschFehler(null);
    if (!neu && !p.dirty) { p.schliessen(); return; }
    if (await formular.aktion() === true) await p.gespeichert();
  }
  async function loeschen(): Promise<void> {
    setLoeschFehler(null);
    if (d.id === null || !(await bestaetigen({ titel: T.confirmDelete(d.name), text: T.deleteConsequence,
      bestaetigenText: T.remove, gefaehrlich: true }))) return;
    try {
      await p.client.deleteUnit(d.id);
      await p.gespeichert();
    } catch (f) {
      setLoeschFehler(meldungAus(f, T.deleteFailed));
    }
  }
  async function abbrechen(): Promise<void> {
    if (laeuft) return;
    if (await darfAbbrechen(p.dirty)) p.schliessen();
  }

  return (
    <Arbeitsdialog titel={p.titel} panel="einheit" onAbbrechen={abbrechen}
      abbrechenGesperrt={laeuft}
      {...(meldung === null ? {} : { meldungen: <Meldung text={meldung} /> })}
      {...(!neu && d.usage === 0 && !d.seeded ? { fussLinks: (
        <KiKnopf art="gefahr" deaktiviert={laeuft} onClick={() => { void loeschen(); }}>{T.remove}</KiKnopf>
      ) } : {})}
      fuss={(
        <>
          <KiKnopf deaktiviert={laeuft} onClick={() => { void abbrechen(); }}>{T.cancel}</KiKnopf>
          <KiKnopf art="primaer" deaktiviert={formular.zustand !== "bereit"} onClick={() => { void speichern(); }}>
            {T.save}
          </KiKnopf>
        </>
      )}>
      <Formular {...formular.formularProps}>
        <label htmlFor="ein-name" data-pflicht="">{T.name}</label>
        <Eingabe id="ein-name" name="name" pflicht wert={d.name} {...fehler("name")}
          onGeaendert={(e) => p.aendere({ name: e.wert })} />
        <label htmlFor="ein-code">{T.code}</label>
        <Eingabe id="ein-code" name="uneceCode" kurz wert={d.uneceCode} hilfe={T.codeHelp} {...fehler("uneceCode")}
          onGeaendert={(e) => p.aendere({ uneceCode: e.wert.toUpperCase() })} />
        <label htmlFor="ein-reihenfolge">{T.sortIndex}</label>
        <Eingabe id="ein-reihenfolge" name="sortIndex" art="zahl" kurz wert={d.sortIndex} hilfe={T.sortHelp}
          {...fehler("sortIndex")} onGeaendert={(e) => p.aendere({ sortIndex: e.wert })} />
        {!neu && <label htmlFor="ein-still">{T.inactiveLabel}</label>}
        {!neu && (
          <Schalter id="ein-still" an={!d.active} beschriftung={T.inactiveEffect}
            onGeaendert={(e) => p.aendere({ active: !e.an })} />
        )}
      </Formular>
    </Arbeitsdialog>
  );
}
